import i18n from '@/i18n';
import { bizDate } from '@/lib/dates';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Loader2 } from 'lucide-react';
import { recruitmentAdminApi } from '@/lib/recruitmentApi';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { useApp } from '@/context/AppContext';
import { VacancyInboxAdmin } from '@/components/recruitment/VacancyInboxAdmin';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

function fmtDate(iso) {
  if (!iso) return '—';
  try { return bizDate(iso, i18n.language); } catch { return iso; }
}

function CreateProcessDialog({ open, onOpenChange, onCreated }) {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [positionTitle, setPositionTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    adminApi.listCompanies({ page: 1, page_size: 200 })
      .then((data) => {
        // listCompanies may return an array OR a {items} envelope.
        const raw = Array.isArray(data) ? data : (data?.items || []);
        const items = raw.filter(
          (c) => (c.suite_codes || []).includes('talentcheck'),
        );
        setClients(items);
        if (items.length) setCompanyId(items[0].id);
      })
      .catch(() => setClients([]));
  }, [open]);

  const submit = async () => {
    if (!companyId || positionTitle.trim().length < 2) {
      toast.error(t('recruitment.createFailed'));
      return;
    }
    setSubmitting(true);
    try {
      const proc = await recruitmentAdminApi.create({
        company_id: companyId,
        position_title: positionTitle.trim(),
        department: department.trim() || null,
      });
      toast.success(t('recruitment.createdToast'));
      onCreated && onCreated(proc);
      setPositionTitle(''); setDepartment('');
      onOpenChange(false);
    } catch (e) {
      toast.error(t('recruitment.createFailed'), { description: formatApiError(e) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="create-process-dialog">
        <DialogHeader>
          <DialogTitle>{t('recruitment.newProcessTitle')}</DialogTitle>
          <DialogDescription>{t('recruitment.newProcessDesc')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-1">
          <div className="space-y-1.5">
            <Label>{t('recruitment.clientField')}</Label>
            {clients.length === 0 ? (
              <p className="text-xs text-muted-foreground">{t('recruitment.noClientsWithSuite')}</p>
            ) : (
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger data-testid="create-process-client">
                  <SelectValue placeholder={t('recruitment.clientPh')} />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>{t('recruitment.positionTitleField')}</Label>
            <Input
              data-testid="create-process-title"
              value={positionTitle}
              onChange={(e) => setPositionTitle(e.target.value)}
              placeholder={t('recruitment.positionTitlePh')}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t('recruitment.departmentField')}</Label>
            <Input
              data-testid="create-process-dept"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              placeholder={t('recruitment.departmentPh')}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            data-testid="create-process-submit"
            disabled={submitting || clients.length === 0}
            onClick={submit}
            className="bg-gradient-primary hover:opacity-90"
          >
            {submitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('recruitment.creating')}</>
            ) : t('recruitment.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminRecruitment() {
  const { t } = useTranslation();
  const { user } = useApp();
  const isAdvisor = user?.role === 'hm_advisor';
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'vacancies' ? 'vacancies' : 'processes';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await recruitmentAdminApi.list();
      setRows(data || []);
    } catch (e) {
      toast.error(t('recruitment.loadFailed'), { description: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      <Topbar
        title={t('recruitment.title')}
        subtitle={t('recruitment.subtitle')}
        actions={
          <Button
            size="sm"
            className="bg-gradient-primary hover:opacity-90 gap-1.5"
            onClick={() => setOpen(true)}
            data-testid="recruitment-new-btn"
          >
            <Plus className="h-4 w-4" /> {t('recruitment.newProcess')}
          </Button>
        }
      />
      <div className="p-6 lg:p-8 space-y-4">
        <Tabs value={tab} onValueChange={(v) => setSearchParams(v === 'vacancies' ? { tab: 'vacancies' } : {})}>
          <TabsList>
            <TabsTrigger value="processes" data-testid="admin-recruitment-tab-processes">
              {t('vacancy.tabProcesses', { defaultValue: 'Procesos' })}
            </TabsTrigger>
            <TabsTrigger value="vacancies" data-testid="admin-recruitment-tab-vacancies">
              {t('vacancy.tabVacanciesAdmin', { defaultValue: 'Solicitudes de Vacantes' })}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        {tab === 'vacancies' ? (
          <VacancyInboxAdmin isAdvisor={isAdvisor} />
        ) : (
        <Card className="shadow-card overflow-hidden">
          {loading ? (
            <div className="py-16 flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
            </div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-sm text-muted-foreground text-center" data-testid="recruitment-empty">
              {t('recruitment.emptyAdmin')}
            </div>
          ) : (
            <Table data-testid="recruitment-table">
              <TableHeader className="bg-muted/40">
                <TableRow>
                  <TableHead>{t('recruitment.thPosition')}</TableHead>
                  <TableHead>{t('recruitment.thClient')}</TableHead>
                  <TableHead>{t('recruitment.thStage')}</TableHead>
                  <TableHead className="hidden md:table-cell">{t('recruitment.thCandidates')}</TableHead>
                  <TableHead className="hidden lg:table-cell">{t('recruitment.thOpened')}</TableHead>
                  <TableHead className="text-right">{t('recruitment.thAction')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`recruitment-row-${r.id}`}>
                    <TableCell className="font-medium">
                      <p>{r.position_title}</p>
                      {r.department && <p className="text-xs text-muted-foreground">{r.department}</p>}
                    </TableCell>
                    <TableCell>{r.company_name}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" data-testid={`recruitment-stage-${r.id}`}>
                        {r.stage}/11 — {t(`recruitment.stages.${r.stage}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell tabular-nums">
                      {(r.assignment_ids || []).length}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                      {fmtDate(r.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Link to={`/admin/recruitment/${r.id}`}>
                        <Button size="sm" variant="outline" data-testid={`recruitment-open-${r.id}`}>
                          {t('recruitment.openCta')}
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
        )}

        <CreateProcessDialog open={open} onOpenChange={setOpen} onCreated={load} />
      </div>
    </>
  );
}
